import React, { useState, useEffect } from 'react';
import { Calculator, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

export const BMICalculator = () => {
  const [height, setHeight] = useState(170);
  const [weight, setWeight] = useState(70);
  const [bmi, setBmi] = useState(0);
  const [category, setCategory] = useState({ label: '', color: '', advice: '' });

  useEffect(() => {
    calculateBMI();
  }, [height, weight]);

  const calculateBMI = () => {
    if (height > 0 && weight > 0) {
      const heightInMeters = height / 100;
      const bmiValue = (weight / (heightInMeters * heightInMeters)).toFixed(1);
      setBmi(bmiValue);

      // Categorize BMI
      if (bmiValue < 18.5) {
        setCategory({
          label: 'Underweight',
          color: 'bg-blue-500',
          advice: 'Consider consulting our nutritionist for a healthy weight gain plan.',
        });
      } else if (bmiValue >= 18.5 && bmiValue < 25) {
        setCategory({
          label: 'Normal Weight',
          color: 'bg-emerald-500',
          advice: 'Great! Maintain your healthy lifestyle with regular check-ups.',
        });
      } else if (bmiValue >= 25 && bmiValue < 30) {
        setCategory({
          label: 'Overweight',
          color: 'bg-amber-500',
          advice: 'Consider our diet and fitness consultation for optimal health.',
        });
      } else {
        setCategory({
          label: 'Obese',
          color: 'bg-red-500',
          advice: 'We recommend scheduling a consultation with our healthcare experts.',
        });
      }
    }
  };

  const handleConsultWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi Ushakiran Medicos, I calculated my BMI as ${bmi} (${category.label}). I would like to consult with your healthcare expert.`
    );
    window.open(`https://wa.me/919007667528?text=${message}`, '_blank');
  };

  return (
    <section
      id="bmi"
      data-testid="bmi-calculator-widget"
      className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            <span className="text-gradient-primary">BMI Calculator</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Check your Body Mass Index and get personalized health recommendations
          </p>
        </div>

        <div className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            {/* Input Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#0284C7] to-[#059669] flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Enter Your Details</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="height" className="text-slate-700 font-medium mb-2 block">
                    Height (cm)
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    data-testid="bmi-height-input"
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    min="100"
                    max="250"
                    className="text-lg py-6 border-slate-300 rounded-xl"
                  />
                  <input
                    type="range"
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    min="100"
                    max="250"
                    className="w-full mt-3 accent-[#0284C7]"
                  />
                </div>

                <div>
                  <Label htmlFor="weight" className="text-slate-700 font-medium mb-2 block">
                    Weight (kg)
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    data-testid="bmi-weight-input"
                    value={weight}
                    onChange={(e) => setWeight(Number(e.target.value))}
                    min="30"
                    max="200"
                    className="text-lg py-6 border-slate-300 rounded-xl"
                  />
                  <input
                    type="range"
                    value={weight}
                    onChange={(e) => setWeight(Number(e.target.value))}
                    min="30"
                    max="200"
                    className="w-full mt-3 accent-[#059669]"
                  />
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200">
                <p className="text-sm text-slate-600 mb-2 text-center">Your BMI</p>
                <div
                  data-testid="bmi-result-value"
                  className="text-5xl sm:text-6xl font-bold text-center text-gradient-primary mb-4"
                >
                  {bmi}
                </div>
                <Badge
                  data-testid="bmi-result-category-badge"
                  className={`${category.color} text-white text-base font-semibold px-4 py-2 w-full justify-center`}
                >
                  {category.label}
                </Badge>
              </div>

              {/* Visual BMI Scale */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex h-3 rounded-full overflow-hidden mb-3">
                  <div className="bg-blue-500 flex-1"></div>
                  <div className="bg-emerald-500 flex-1"></div>
                  <div className="bg-amber-500 flex-1"></div>
                  <div className="bg-red-500 flex-1"></div>
                </div>
                <div className="grid grid-cols-4 gap-1 text-xs text-slate-600 text-center">
                  <span>&lt;18.5</span>
                  <span>18.5-25</span>
                  <span>25-30</span>
                  <span>30+</span>
                </div>
              </div>

              {/* Advice */}
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="text-sm text-slate-700 leading-relaxed">{category.advice}</p>
              </div>

              {/* CTA */}
              <Button
                data-testid="bmi-consult-us-button"
                onClick={handleConsultWhatsApp}
                className="w-full bg-gradient-to-r from-[#059669] to-[#047857] text-white hover:shadow-xl py-6 text-base font-semibold transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Consult Us on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};